import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  Grid,
  Typography,
  makeStyles,
  withStyles,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  Divider,
  Badge,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  InputAdornment,
  Popover,
  Tabs,
  Tab
} from '@material-ui/core';
import { Pagination, Autocomplete } from '@material-ui/lab';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import axiosInterceptor from 'src/app/axiosInterceptor';
import { _convertObjectToQuery } from 'src/app/utils/apiService';
import { showGlobalToast } from 'src/features/uiSlice';
import { getListDeviceType } from 'src/features/deviceTypeSlice';
import { getListAgencies } from 'src/features/agencySlice';
import { getListUsers } from 'src/features/userSlice';
import { roles } from 'src/app/constant/roles';
import { messageToastType_const } from 'src/app/constant/config';

// Icons
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import SearchIcon from '@material-ui/icons/Search';
import AddIcon from '@material-ui/icons/Add';
import PhotoLibraryIcon from '@material-ui/icons/PhotoLibrary';
import PrintIcon from '@material-ui/icons/Print';
import EditIcon from '@material-ui/icons/Edit';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import ClearIcon from '@material-ui/icons/Clear';
import PhotoCameraIcon from '@material-ui/icons/PhotoCamera';
import DeleteIcon from '@material-ui/icons/Delete';
import FilterListIcon from '@material-ui/icons/FilterList';
import CloseIcon from '@material-ui/icons/Close';

// Helper format tiền tệ VNĐ
const formatMoney = amount => {
  if (!amount && amount !== 0) return '0 đ';
  return `${amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')} đ`;
};

// Helper format số tiền nhập trong ô input (thêm dấu . hàng nghìn)
const formatInputNumber = val => {
  if (val === null || val === undefined || val === '' || val === 0 || val === '0') return '';
  const numStr = val.toString().replace(/\D/g, '');
  if (!numStr) return '';
  return numStr.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

// Helper parse số tiền nhập trong ô input từ chuỗi thành Number
const parseInputNumber = str => {
  if (!str) return 0;
  const numStr = str.toString().replace(/\D/g, '');
  return numStr ? Number(numStr) : 0;
};

// Helper get image URL (xử lý link tuyệt đối, relative path từ Minio / Server)
const getImageUrl = url => {
  if (!url || typeof url !== 'string') return '';
  if (
    url.startsWith('http://') ||
    url.startsWith('https://') ||
    url.startsWith('data:') ||
    url.startsWith('blob:')
  ) {
    return url;
  }
  const cleanServer = (process.env.REACT_APP_IMG_SERVER || process.env.REACT_APP_API_URL || '').replace(/\/+$/, '').replace(/\/api\/v\d+$/, '');
  const cleanUrl = url.replace(/^\/+/, '');
  return cleanServer ? `${cleanServer}/${cleanUrl}` : `/${cleanUrl}`;
};

// Styled Table Cell - Đồng bộ với OrdersTable
const StyledTableCell = withStyles(() => ({
  head: {
    backgroundColor: '#f8fafc',
    color: '#334155',
    fontWeight: 700,
    fontSize: 13,
    whiteSpace: 'nowrap',
    borderBottom: '2px solid #e2e8f0',
    verticalAlign: 'middle'
  },
  body: {
    fontSize: 13,
    whiteSpace: 'nowrap',
    color: '#334155',
    verticalAlign: 'top'
  },
  root: { padding: '12px 14px' }
}))(TableCell);

const useStyles = makeStyles(theme => ({
  root: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(3)
  },
  summaryRow: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
    marginBottom: 16
  },
  summaryChip: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 12px',
    borderRadius: 20,
    border: '1px solid'
  },
  summaryDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    flexShrink: 0
  },
  filterCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    borderRadius: 8,
    boxShadow: 'none',
    border: '1px solid #e2e8f0'
  },
  searchInput: {
    flex: '1 1 240px',
    minWidth: 200,
    maxWidth: 360,
    '& .MuiOutlinedInput-root': {
      borderRadius: 8
    }
  },
  filterSelect: {
    minWidth: 150,
    '& .MuiOutlinedInput-root': {
      borderRadius: 8
    },
    '& .MuiInputLabel-root': {
      fontSize: 13
    },
    '& .MuiOutlinedInput-input': {
      fontSize: 13,
      padding: '10.5px 14px'
    }
  },
  tableCard: {
    boxShadow: 'none',
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    overflow: 'hidden'
  },
  tableRow: {
    '&:hover': {
      backgroundColor: '#f8fafc !important',
      '& $stickyColFirst, & $stickyColSecond, & $stickyColThird': {
        backgroundColor: '#f8fafc !important'
      }
    }
  },
  stickyColFirst: {
    position: 'sticky',
    left: 0,
    backgroundColor: '#ffffff !important',
    zIndex: 10
  },
  stickyColSecond: {
    position: 'sticky',
    left: 100,
    backgroundColor: '#ffffff !important',
    zIndex: 10
  },
  stickyColThird: {
    position: 'sticky',
    left: 230,
    backgroundColor: '#ffffff !important',
    zIndex: 10
  },
  stickyHeader: {
    position: 'sticky',
    zIndex: 11,
    backgroundColor: '#f8fafc !important'
  },
  printHeader: {
    textAlign: 'center',
    marginBottom: 20,
    paddingBottom: 15,
    borderBottom: '2px double #cbd5e1'
  },
  printSection: {
    marginBottom: 16,
    padding: 12,
    backgroundColor: '#f8fafc',
    borderRadius: 8,
    border: '1px solid #e2e8f0'
  }
}));

// Config Trạng thái Phiếu lắp đặt - Chuẩn BE Spec
const INSTALLATION_STATUS = {
  0: { label: 'Chờ nhận phiếu', color: '#d97706', bg: '#fef3c7' },
  1: { label: 'Đang lắp đặt', color: '#2563eb', bg: '#eff6ff' },
  2: { label: 'Chờ Kế toán đối soát', color: '#7c3aed', bg: '#f5f3ff' },
  4: { label: 'Hoàn thành đối soát', color: '#16a34a', bg: '#f0fdf4' },
  '-1': { label: 'Đã hủy', color: '#64748b', bg: '#f1f5f9' }
};

// Config Trạng thái Tiền mặt / Chuyển khoản
const PAYMENT_STATUS_CONFIG = {
  UNPAID: { label: 'Chưa thu tiền', color: '#d97706', bg: '#fef3c7' },
  KTV_COLLECTED: { label: 'KTV đã thu tiền mặt', color: '#2563eb', bg: '#eff6ff' },
  SALE_RECEIVED: { label: 'Khách đã CK cho Sale', color: '#7c3aed', bg: '#f5f3ff' },
  RECONCILED: { label: 'Đã đối soát hoàn tất', color: '#16a34a', bg: '#f0fdf4' }
};

// Danh mục Mã thiết bị mặc định (Đồng bộ với hệ thống VGPS)
const DEFAULT_DEVICE_CODE_OPTIONS = [
  { code: 'VGPS_CAR4G', name: 'VGPS_CAR4G' },
  { code: 'VGPS_MOTO4G', name: 'VGPS_MOTO4G' },
  { code: 'VGPS_HUM4G', name: 'VGPS_HUM4G' },
  { code: 'VGPS_MAG4G', name: 'VGPS_MAG4G' },
  { code: 'VGPS_ODB4G', name: 'VGPS_ODB4G' },
  { code: 'VGPS_2CAM', name: 'VGPS_2CAM' },
  { code: 'VGPS_4CAM', name: 'VGPS_4CAM' },
  { code: 'VGPS_CAM4G', name: 'VGPS_CAM4G' }
];

// Helper Convert BE Ticket -> FE Ticket Format
const mapApiTicketToFe = item => {
  if (!item) return null;
  return {
    id: item.ticket_code || item.code || item.id,
    rawId: item.id,
    orderCode: item.order_code || item.orderCode || '',
    createdAt: item.created_at
      ? moment(item.created_at).format('DD/MM/YYYY HH:mm')
      : item.createdAt || '',
    createdBySale:
      item.created_by_sale_name ||
      item.created_by_sale ||
      item.createdBySale ||
      'Sale Online',
    customerName: item.customer_name || item.customerName || '',
    customerPhone: item.customer_phone || item.customerPhone || '',
    targetType: item.target_type || item.targetType || 'VNT',
    address: item.address || '',
    vehicleType: item.vehicle_type || item.vehicleType || '',
    licensePlate: item.license_plate || item.licensePlate || '',
    deviceCode: item.device_code || item.deviceCode || item.device_type_name || item.deviceTypeName || '',
    deviceTypeName: item.device_type_name || item.deviceTypeName || '',
    hasFuelSensor: Boolean(item.has_fuel_sensor ?? item.hasFuelSensor),
    hasTempSensor: Boolean(item.has_temp_sensor ?? item.hasTempSensor),
    serialImei: item.serial_imei || item.serialImei || '--',
    technician: item.technician_name || item.technician || 'Chưa nhận',
    techPhone: item.tech_phone || item.techPhone || '',
    scheduledTime: item.scheduled_time
      ? typeof item.scheduled_time === 'string' && item.scheduled_time.includes('T')
        ? item.scheduled_time.replace('T', ' ').slice(0, 16)
        : item.scheduled_time
      : '',
    completedTime: item.completed_time
      ? typeof item.completed_time === 'string' && item.completed_time.includes('T')
        ? item.completed_time.replace('T', ' ').slice(0, 16)
        : item.completed_time
      : '',
    status: item.status !== undefined ? Number(item.status) : 0,
    paymentMethod: item.payment_method || item.paymentMethod || 'CASH_KTV',
    paymentStatus: item.payment_status || item.paymentStatus || 'UNPAID',
    fee: Number(item.fee) || 0,
    cashCollectedByTech:
      Number(item.cash_collected_by_tech ?? item.cashCollectedByTech) || 0,
    notes: item.notes || '',
    customerRating: item.customer_rating || item.customerRating || null,
    images: (item.images || []).map(img => {
      if (typeof img === 'string') return { id: img, url: getImageUrl(img) };
      const rawUrl = img.url || img.image_url || img.path || img.file_path || '';
      return {
        id: img.id || img.image_id || rawUrl,
        url: getImageUrl(rawUrl)
      };
    })
  };
};





const InstallationsTable = () => {
  const classes = useStyles();
  const dispatch = useDispatch();

  // Redux Auth & DeviceType State
  const {
    isAdmin,
    isAccountant,
    isSale,
    isCustomerCare,
    isTechnician,
    isLeadMarketing,
    dataLogin
  } = useSelector(state => state.authSlice || {});
  const { listDeviceType, statusGetListDeviceType } = useSelector(state => state.deviceTypeSlice || {});
  const [ktvStaffList, setKtvStaffList] = useState([]);

  // Load danh mục Loại thiết bị từ Redux
  useEffect(() => {
    if (
      (!listDeviceType || listDeviceType.length === 0) &&
      statusGetListDeviceType !== 'PENDING' &&
      statusGetListDeviceType !== 'SUCCESS'
    ) {
      dispatch(getListDeviceType());
    }
  }, [dispatch, listDeviceType, statusGetListDeviceType]);

  // Load danh sách User từ API /agencies/user/get với role_id = 3 (CSKH) & role_id = 8 (KTV)
  useEffect(() => {
    const fetchKtvUsers = async () => {
      try {
        const [res3, res8] = await Promise.all([
          axiosInterceptor.get('/agencies/user/get?page=1&page_size=1000&role_id=3'),
          axiosInterceptor.get('/agencies/user/get?page=1&page_size=1000&role_id=8')
        ]);

        const users3 = res3?.data?.payload?.users || res3?.data?.payload?.data || (Array.isArray(res3?.data?.payload) ? res3?.data?.payload : []);
        const users8 = res8?.data?.payload?.users || res8?.data?.payload?.data || (Array.isArray(res8?.data?.payload) ? res8?.data?.payload : []);

        setKtvStaffList([...users3, ...users8]);
      } catch (err) {
        console.error('Fetch KTV users error:', err);
      }
    };

    fetchKtvUsers();
  }, []);

  // Danh mục mã thiết bị động (Load từ Redux listDeviceType hoặc mặc định)
  const availableDeviceOptions = useMemo(() => {
    if (listDeviceType && Array.isArray(listDeviceType) && listDeviceType.length > 0) {
      return listDeviceType.map(dt => {
        const val = dt.name || dt.code || dt.device_code || 'VGPS_CAR4G';
        return {
          code: val,
          name: val,
          id: dt.id
        };
      });
    }
    return DEFAULT_DEVICE_CODE_OPTIONS;
  }, [listDeviceType]);

  // Quyền nhận phiếu: KTV, CSKH, tài khoản huydv, hoặc Admin
  const canClaimTicket = useMemo(() => {
    const username = dataLogin?.username || dataLogin?.account || '';
    return (
      isAdmin ||
      isCustomerCare ||
      isTechnician ||
      username.toLowerCase() === 'huydv'
    );
  }, [isAdmin, isCustomerCare, isTechnician, dataLogin]);

  // Quyền lập phiếu: Sale, Admin, Lead Marketing
  const canCreateTicket = useMemo(() => {
    return isAdmin || isSale || isLeadMarketing;
  }, [isAdmin, isSale, isLeadMarketing]);

  // Quyền đối soát Kế toán: Accountant, Admin
  const canReconcile = useMemo(() => {
    return isAdmin || isAccountant;
  }, [isAdmin, isAccountant]);

  // State & Ref chính
  const [installations, setInstallations] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submittingForm, setSubmittingForm] = useState(false);
  const isSubmittingRef = useRef(false);
  const [apiStats, setApiStats] = useState(null);

  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterTech, setFilterTech] = useState('all');
  const [filterTarget, setFilterTarget] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);

  const [filterAnchorEl, setFilterAnchorEl] = useState(null);

  const handleFilterClick = event => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const activeFilterCount = useMemo(() => {
    return [filterTarget !== 'all', filterTech !== 'all'].filter(Boolean).length;
  }, [filterTarget, filterTech]);

  // Horizontal Drag State
  const scrollContainerRef = useRef(null);
  const [isDown, setIsDown] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [isScrolled, setIsScrolled] = useState(false);

  const onMouseDown = e => {
    setIsDown(true);
    setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
    setScrollLeft(scrollContainerRef.current.scrollLeft);
  };
  const onMouseLeave = () => setIsDown(false);
  const onMouseUp = () => setIsDown(false);
  const onMouseMove = e => {
    if (!isDown) return;
    e.preventDefault();
    const x = e.pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - startX) * 1.5;
    scrollContainerRef.current.scrollLeft = scrollLeft - walk;
    handleScroll();
  };

  const handleScroll = () => {
    if (scrollContainerRef.current) {
      setIsScrolled(scrollContainerRef.current.scrollLeft > 0);
    }
  };

  // Modals state
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [openGalleryModal, setOpenGalleryModal] = useState(false);
  const [openPrintModal, setOpenPrintModal] = useState(false);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [ticketToDelete, setTicketToDelete] = useState(null);

  // Customer Search Autocomplete State
  const [tabForm, setTabForm] = useState(0);
  const [fetchType, setFetchType] = useState('account');
  const [suggestionLoading, setSuggestionLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const searchTimeout = useRef(null);

  const { listAgencies } = useSelector(state => state.agencySlice || {});
  const { listUsers } = useSelector(state => state.userSlice || {});

  useEffect(() => {
    if (openAddModal) {
      setSuggestionLoading(true);
      const action = tabForm === 0 ? getListAgencies : getListUsers;
      dispatch(
        action({ page: 1, page_size: 1000 })
      ).finally(() => setSuggestionLoading(false));
    }
  }, [openAddModal, tabForm, dispatch]);

  const handleFetchUserInfo = selectedOption => {
    setSelectedUser(selectedOption);
    if (!selectedOption) {
      return;
    }

    if (tabForm === 0) {
      const agency = selectedOption;
      setForm(prev => ({
        ...prev,
        targetType: 'AGENCY',
        customerName: agency.name || agency.fullname || '',
        customerPhone: agency.leader?.phone || agency.phone || '',
        address: agency.leader?.address || agency.address || ''
      }));
    } else {
      const user = selectedOption;
      setForm(prev => ({
        ...prev,
        targetType: 'VNT',
        customerName: user.full_name || user.fullname || user.username || '',
        customerPhone: user.phone || '',
        address: user.agency?.name || user.address || ''
      }));
    }
  };

  const handleAutocompleteSearch = keywords => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);

    if (!keywords || keywords.trim().length === 0) {
      setSuggestionLoading(true);
      const searchAction = tabForm === 0 ? getListAgencies : getListUsers;
      dispatch(
        searchAction({ page: 1, page_size: 1000 })
      ).finally(() => setSuggestionLoading(false));
      return;
    }

    if (keywords.trim().length < 2) {
      setSuggestionLoading(false);
      return;
    }

    setSuggestionLoading(true);
    searchTimeout.current = setTimeout(() => {
      const searchAction = tabForm === 0 ? getListAgencies : getListUsers;
      dispatch(
        searchAction({ keywords, page: 1, page_size: 1000 })
      ).finally(() => setSuggestionLoading(false));
    }, 500);
  };

  // Upload Ảnh Nghiệm Thu state & ref
  const fileInputRef = useRef(null);
  const [ticketForUpload, setTicketForUpload] = useState(null);

  // 1. Fetch Danh sách phiếu lắp đặt từ BE API
  const fetchListInstallations = useCallback(async () => {
    setLoading(true);
    try {
      const queryParams = {
        page: page + 1,
        page_size: rowsPerPage,
        keywords: search || undefined,
        status: filterStatus !== 'all' ? filterStatus : undefined,
        target_type: filterTarget !== 'all' ? filterTarget : undefined,
        technician: filterTech !== 'all' ? filterTech : undefined,
        from_date: fromDate ? moment(fromDate).format('YYYY-MM-DD') : undefined,
        to_date: toDate ? moment(toDate).format('YYYY-MM-DD') : undefined
      };

      const queryStr = _convertObjectToQuery(queryParams);
      const res = await axiosInterceptor.get(`/installations?${queryStr}`);

      if (res.data && (res.data.success || res.data.payload)) {
        const payload = res.data.payload || res.data;
        const items = payload.items || payload.data || (Array.isArray(payload) ? payload : []);
        const mapped = items.map(mapApiTicketToFe);

        setInstallations(mapped);
        setTotalCount(payload.total || mapped.length);
        if (payload.stats) {
          setApiStats({
            feeSum: payload.stats.total_fee_sum || 0,
            cashSum: payload.stats.total_cash_sum || 0,
            countsByStatus: payload.stats.counts_by_status || {}
          });
        }
        return mapped;
      }
    } catch (err) {
      console.error('Fetch installations error:', err);
    } finally {
      setLoading(false);
    }
    return [];
  }, [page, rowsPerPage, search, filterStatus, filterTarget, filterTech, fromDate, toDate]);

  useEffect(() => {
    fetchListInstallations();
  }, [fetchListInstallations]);

  // Form State cho Tạo/Sửa phiếu lắp đặt (Prefix I-*)
  const [form, setForm] = useState({
    id: '',
    rawId: null,
    orderCode: '',
    customerName: '',
    customerPhone: '',
    targetType: 'VNT', // 'VNT' | 'AGENCY'
    address: '',
    vehicleType: '',
    licensePlate: '',
    deviceCode: '',
    deviceTypeName: '',
    hasFuelSensor: false,
    hasTempSensor: false,
    serialImei: '',
    technician: 'Chưa nhận',
    techPhone: '',
    scheduledTime: '',
    paymentMethod: 'CASH_KTV', // 'CASH_KTV' | 'BANK_SALE'
    paymentStatus: 'UNPAID',
    fee: 0,
    cashCollectedByTech: 0,
    notes: '',
    createdBySale: ''
  });

  // Cập nhật khi chọn Mã thiết bị khác
  const handleSelectDeviceCode = selectedCode => {
    setForm(prev => ({
      ...prev,
      deviceCode: selectedCode,
      deviceTypeName: selectedCode
    }));
  };

  // Cập nhật khi chọn thêm Cảm biến
  const handleToggleSensor = (sensorType, isChecked) => {
    setForm(prev => ({
      ...prev,
      hasFuelSensor: sensorType === 'fuel' ? isChecked : prev.hasFuelSensor,
      hasTempSensor: sensorType === 'temp' ? isChecked : prev.hasTempSensor
    }));
  };

  // 2. Thống kê theo Trạng thái
  const countsByStatus = useMemo(() => {
    if (apiStats && apiStats.countsByStatus) {
      return apiStats.countsByStatus;
    }

    return {
      0: installations.filter(i => i.status === 0).length,
      1: installations.filter(i => i.status === 1).length,
      2: installations.filter(i => i.status === 2).length,
      4: installations.filter(i => i.status === 4).length,
      '-1': installations.filter(i => i.status === -1).length
    };
  }, [apiStats, installations]);

  // Trigger file upload cho Nghiệm thu
  const handleTriggerUpload = ticket => {
    setTicketForUpload(ticket);
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Upload file nghiệm thu (API 6)
  const handleFileChange = async e => {
    const targetInput = e.target;
    if (!targetInput || !targetInput.files || targetInput.files.length === 0 || !ticketForUpload) return;
    const files = Array.from(targetInput.files);

    try {
      const ticketId = ticketForUpload.rawId || ticketForUpload.id;
      const formData = new FormData();
      files.forEach(f => formData.append('images', f));

      const res = await axiosInterceptor.post(`/installations/${ticketId}/images`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      dispatch(
        showGlobalToast({
          message: `Tải lên ${files.length} ảnh nghiệm thu thành công!`,
          type: messageToastType_const.success
        })
      );
      const latestItems = await fetchListInstallations();

      const curTicketId = ticketForUpload.rawId || ticketForUpload.id;
      if (latestItems && Array.isArray(latestItems)) {
        const updatedTicket = latestItems.find(t => t.rawId === curTicketId || t.id === curTicketId);
        if (updatedTicket) {
          setSelectedTicket(updatedTicket);
        }
      } else if (res.data && res.data.payload) {
        const newImgs = Array.isArray(res.data.payload) ? res.data.payload : (res.data.payload.images || []);
        if (newImgs.length > 0) {
          const mappedNew = newImgs.map(img => typeof img === 'string' ? { id: img, url: getImageUrl(img) } : { id: img.id || img.url, url: getImageUrl(img.url || img.image_url) });
          setSelectedTicket(prev => prev ? { ...prev, images: [...(prev.images || []), ...mappedNew] } : prev);
        }
      }
    } catch (err) {
      console.error('Upload images error:', err);
      dispatch(
        showGlobalToast({
          message: 'Tải ảnh thất bại: ' + (err.response?.data?.message || err.message),
          type: messageToastType_const.error
        })
      );
    } finally {
      if (targetInput) {
        targetInput.value = '';
      }
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Xóa 1 ảnh nghiệm thu (API 6b: DELETE /installations/{id}/images/{imageId})
  const handleDeleteImageFromTicket = async (ticket, imgItem, imgIndex) => {
    if (!ticket) return;
    const ticketId = ticket.rawId || ticket.id;
    const imageId = typeof imgItem === 'object' && imgItem.id ? imgItem.id : (typeof imgItem === 'string' ? imgItem : imgIndex);

    try {
      await axiosInterceptor.delete(`/installations/${ticketId}/images/${encodeURIComponent(imageId)}`);
      dispatch(
        showGlobalToast({
          message: 'Đã xóa ảnh nghiệm thu thành công!',
          type: messageToastType_const.success
        })
      );
      const latestItems = await fetchListInstallations();
      const curTicketId = ticket.rawId || ticket.id;
      if (latestItems && Array.isArray(latestItems)) {
        const updatedTicket = latestItems.find(t => t.rawId === curTicketId || t.id === curTicketId);
        if (updatedTicket) {
          setSelectedTicket(updatedTicket);
          return;
        }
      }
      if (selectedTicket && (selectedTicket.id === ticket.id || selectedTicket.rawId === ticket.rawId)) {
        setSelectedTicket(prev => ({
          ...prev,
          images: (prev.images || []).filter((_, idx) => idx !== imgIndex)
        }));
      }
    } catch (err) {
      console.error('Delete image error:', err);
      dispatch(
        showGlobalToast({
          message: 'Xóa ảnh thất bại: ' + (err.response?.data?.message || err.message),
          type: messageToastType_const.error
        })
      );
    }
  };

  // Handle Actions Menu
  const handleMenuOpen = (e, ticket) => {
    setAnchorEl(e.currentTarget);
    setSelectedTicket(ticket);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Mở modal tạo mới (Dành cho Sale, Admin)
  const handleOpenAddModal = () => {
    if (!canCreateTicket) {
      dispatch(
        showGlobalToast({
          message: 'Tài khoản Sale hoặc Admin mới có quyền khởi tạo phiếu lắp đặt!',
          type: messageToastType_const.warning
        })
      );
      return;
    }
    const currentUserName =
      dataLogin?.full_name || dataLogin?.name || dataLogin?.username || 'Sale Online';

    const defaultDevCode = availableDeviceOptions[0]?.code || 'VGPS_CAR4G';

    setIsEditing(false);
    isSubmittingRef.current = false;
    setTabForm(0);
    setSelectedUser(null);
    setFetchType('account');
    setForm({
      id: '',
      rawId: null,
      orderCode: '',
      customerName: '',
      customerPhone: '',
      targetType: 'AGENCY',
      address: '',
      vehicleType: '',
      licensePlate: '',
      deviceCode: '',
      deviceTypeName: '',
      hasFuelSensor: false,
      hasTempSensor: false,
      serialImei: '',
      technician: 'Chưa nhận',
      techPhone: '',
      scheduledTime: new Date().toISOString().slice(0, 16),
      paymentMethod: 'CASH_KTV',
      paymentStatus: 'UNPAID',
      fee: 0,
      cashCollectedByTech: 0,
      notes: '',
      createdBySale: currentUserName
    });
    setOpenAddModal(true);
  };

  // Mở modal chỉnh sửa
  const handleEditTicket = ticket => {
    setIsEditing(true);
    setTabForm(ticket?.targetType === 'AGENCY' ? 0 : 1);
    setSelectedUser(null);
    setFetchType('account');
    setForm({ ...ticket });
    setOpenAddModal(true);
    handleMenuClose();
  };

  // Lưu Form Tạo / Sửa (API 2 & 8)
  const handleSaveForm = async () => {
    if (isSubmittingRef.current || submittingForm) return;

    if (!form.customerName || !form.customerPhone) {
      dispatch(
        showGlobalToast({
          message: 'Vui lòng nhập Tên khách hàng và Số điện thoại!',
          type: messageToastType_const.warning
        })
      );
      return;
    }

    isSubmittingRef.current = true;
    setSubmittingForm(true);

    const payloadBody = {
      target_type: form.targetType,
      customer_name: form.customerName,
      customer_phone: form.customerPhone,
      address: form.address,
      vehicle_type: form.vehicleType,
      license_plate: form.licensePlate,
      device_code: form.deviceCode,
      has_fuel_sensor: form.hasFuelSensor,
      has_temp_sensor: form.hasTempSensor,
      serial_imei: form.serialImei,
      scheduled_time: form.scheduledTime ? moment(form.scheduledTime).format('YYYY-MM-DD HH:mm:ss') : undefined,
      payment_method: form.paymentMethod,
      fee: Number(form.fee) || 0,
      cash_collected_by_tech: Number(form.cashCollectedByTech) || 0,
      notes: form.notes
    };

    if (isEditing) {
      try {
        const ticketId = form.rawId || form.id;
        await axiosInterceptor.put(`/installations/${ticketId}`, payloadBody);
        dispatch(
          showGlobalToast({
            message: `Cập nhật phiếu lắp đặt ${form.id} thành công!`,
            type: messageToastType_const.success
          })
        );
        fetchListInstallations();
        setOpenAddModal(false);
      } catch (err) {
        console.error('Update ticket error:', err);
        dispatch(
          showGlobalToast({
            message: 'Cập nhật thất bại: ' + (err.response?.data?.message || err.message),
            type: messageToastType_const.error
          })
        );
      } finally {
        isSubmittingRef.current = false;
        setSubmittingForm(false);
      }
    } else {
      try {
        await axiosInterceptor.post('/installations', payloadBody);
        dispatch(
          showGlobalToast({
            message: 'Sale đã lập phiếu lắp đặt mới thành công!',
            type: messageToastType_const.success
          })
        );
        fetchListInstallations();
        setOpenAddModal(false);
      } catch (err) {
        console.error('Create ticket error:', err);
        dispatch(
          showGlobalToast({
            message: 'Tạo phiếu thất bại: ' + (err.response?.data?.message || err.message),
            type: messageToastType_const.error
          })
        );
      } finally {
        isSubmittingRef.current = false;
        setSubmittingForm(false);
      }
    }
  };

  // 1. Thao tác NHẬN PHIẾU (API 3)
  const handleClaimTicket = async ticket => {
    if (!canClaimTicket) {
      dispatch(
        showGlobalToast({
          message: 'Chỉ tài khoản CSKH hoặc Kỹ thuật viên (huydv) mới được nhận phiếu!',
          type: messageToastType_const.warning
        })
      );
      return;
    }

    try {
      const ticketId = ticket.rawId || ticket.id;
      await axiosInterceptor.put(`/installations/${ticketId}/claim`);
      dispatch(
        showGlobalToast({
          message: `Đã nhận phiếu ${ticket.id}! Bạn là KTV phụ trách thi công.`,
          type: messageToastType_const.success
        })
      );
      fetchListInstallations();
    } catch (err) {
      console.error('Claim ticket error:', err);
      dispatch(
        showGlobalToast({
          message: 'Nhận phiếu thất bại: ' + (err.response?.data?.message || err.message),
          type: messageToastType_const.error
        })
      );
    }
  };

  // 1b. Thao tác HỦY NHẬN PHIẾU (NHẢ PHIẾU VỀ TRẠNG THÁI CHỜ NHẬN)
  const handleUnclaimTicket = async ticket => {
    if (!canClaimTicket) {
      dispatch(
        showGlobalToast({
          message: 'Bạn không có quyền nhả phiếu này!',
          type: messageToastType_const.warning
        })
      );
      return;
    }

    try {
      const ticketId = ticket.rawId || ticket.id;
      let success = false;
      let lastErr = null;

      const unclaimMethods = [
        () => axiosInterceptor.put(`/installations/${ticketId}/unclaim`),
        () => axiosInterceptor.post(`/installations/${ticketId}/unclaim`),
        () => axiosInterceptor.delete(`/installations/${ticketId}/claim`),
        () => axiosInterceptor.put(`/installations/${ticketId}/status`, { status: 0 }),
        () => axiosInterceptor.patch(`/installations/${ticketId}/status`, { status: 0 })
      ];

      for (const method of unclaimMethods) {
        try {
          await method();
          success = true;
          break;
        } catch (err) {
          lastErr = err;
        }
      }

      if (!success) {
        throw lastErr;
      }

      dispatch(
        showGlobalToast({
          message: `Đã hủy nhận phiếu ${ticket.id}. Phiếu đã quay về trạng thái Chờ nhận!`,
          type: messageToastType_const.info
        })
      );
      fetchListInstallations();
    } catch (err) {
      console.error('Unclaim ticket error:', err);
      dispatch(
        showGlobalToast({
          message: 'Hủy nhận phiếu thất bại: ' + (err.response?.data?.message || err.message),
          type: messageToastType_const.error
        })
      );
    }
  };

  // 2. Thao tác KTV BÁO HOÀN THÀNH THI CÔNG & NỘP ĐỐI SOÁT (API 4)
  const handleSubmitReconciliation = async ticket => {
    try {
      const ticketId = ticket.rawId || ticket.id;
      await axiosInterceptor.put(`/installations/${ticketId}/submit-reconciliation`);
      dispatch(
        showGlobalToast({
          message: `Phiếu ${ticket.id} đã nộp báo cáo thi công. Chờ Kế toán đối soát tiền!`,
          type: messageToastType_const.info
        })
      );
      fetchListInstallations();
    } catch (err) {
      console.error('Submit reconciliation error:', err);
      dispatch(
        showGlobalToast({
          message: 'Nộp báo cáo thất bại: ' + (err.response?.data?.message || err.message),
          type: messageToastType_const.error
        })
      );
    }
    handleMenuClose();
  };

  // 3. Thao tác KẾ TOÁN DUYỆT ĐỐI SOÁT & HOÀN THÀNH (API 5)
  const handleReconcileComplete = async ticket => {
    if (!canReconcile) {
      dispatch(
        showGlobalToast({
          message: 'Chỉ Kế toán mới có quyền duyệt đối soát tiền và hoàn thành phiếu!',
          type: messageToastType_const.warning
        })
      );
      return;
    }

    try {
      const ticketId = ticket.rawId || ticket.id;
      await axiosInterceptor.put(`/installations/${ticketId}/reconcile`);
      dispatch(
        showGlobalToast({
          message: `Kế toán đã duyệt đối soát tiền & hoàn thành phiếu ${ticket.id}!`,
          type: messageToastType_const.success
        })
      );
      fetchListInstallations();
    } catch (err) {
      console.error('Reconcile ticket error:', err);
      dispatch(
        showGlobalToast({
          message: 'Duyệt đối soát thất bại: ' + (err.response?.data?.message || err.message),
          type: messageToastType_const.error
        })
      );
    }
    handleMenuClose();
  };

  // 4. Thao tác HỦY PHIẾU (API 7)
  const handleCancelTicket = async ticket => {
    try {
      const ticketId = ticket.rawId || ticket.id;
      await axiosInterceptor.put(`/installations/${ticketId}/cancel`);
      dispatch(
        showGlobalToast({
          message: `Đã chuyển phiếu ${ticket.id} sang trạng thái Đã hủy!`,
          type: messageToastType_const.info
        })
      );
      fetchListInstallations();
    } catch (err) {
      console.error('Cancel ticket error:', err);
      dispatch(
        showGlobalToast({
          message: 'Hủy phiếu thất bại: ' + (err.response?.data?.message || err.message),
          type: messageToastType_const.error
        })
      );
    }
    handleMenuClose();
  };

  const techOptions = useMemo(() => {
    const optionsMap = new Map();

    // 1. Lấy tất cả user từ API /agencies/user/get với role_id = 3 (CSKH) & role_id = 8 (KTV)
    if (Array.isArray(ktvStaffList) && ktvStaffList.length > 0) {
      ktvStaffList.forEach(u => {
        const name = u.full_name || u.name || u.username;
        if (name) {
          optionsMap.set(name.trim(), name.trim());
        }
      });
    }

    // 2. Thêm bất kỳ KTV nào đã xuất hiện trên phiếu lắp đặt hiện tại
    if (Array.isArray(installations)) {
      installations.forEach(i => {
        if (i.technician && i.technician !== 'Chưa nhận' && i.technician !== '--') {
          const trimmed = i.technician.trim();
          if (!optionsMap.has(trimmed)) {
            optionsMap.set(trimmed, trimmed);
          }
        }
      });
    }

    return Array.from(optionsMap.values());
  }, [ktvStaffList, installations]);

  const activeDisplayList = useMemo(() => {
    if (!installations || !Array.isArray(installations)) return [];
    if (filterTech === 'all') return installations;
    if (filterTech === 'Chưa nhận') {
      return installations.filter(
        i => !i.technician || i.technician === 'Chưa nhận' || i.technician === '--'
      );
    }
    return installations.filter(i => {
      if (!i.technician) return false;
      const tech = i.technician.toLowerCase();
      const target = filterTech.toLowerCase();
      return tech === target || tech.includes(target);
    });
  }, [installations, filterTech]);

  // Selection state cho Xóa nhiều phiếu (Admin)
  const [selectedTicketIds, setSelectedTicketIds] = useState([]);

  const isAllSelected = useMemo(() => {
    if (!activeDisplayList || activeDisplayList.length === 0) return false;
    return activeDisplayList.every(item => selectedTicketIds.includes(item.rawId || item.id));
  }, [activeDisplayList, selectedTicketIds]);

  const isSomeSelected = useMemo(() => {
    if (!activeDisplayList || activeDisplayList.length === 0) return false;
    return activeDisplayList.some(item => selectedTicketIds.includes(item.rawId || item.id)) && !isAllSelected;
  }, [activeDisplayList, selectedTicketIds, isAllSelected]);

  const handleSelectAll = e => {
    if (e.target.checked) {
      const allIds = activeDisplayList.map(item => item.rawId || item.id);
      setSelectedTicketIds(allIds);
    } else {
      setSelectedTicketIds([]);
    }
  };

  const handleSelectRow = (e, ticketId) => {
    e.stopPropagation();
    if (selectedTicketIds.includes(ticketId)) {
      setSelectedTicketIds(prev => prev.filter(id => id !== ticketId));
    } else {
      setSelectedTicketIds(prev => [...prev, ticketId]);
    }
  };

  // 5. Thao tác XÓA PHIẾU LẮP ĐẶT (API: DELETE /installations với payload { ids: [...] } - Chỉ Admin)
  const handleOpenSingleDeleteConfirm = ticket => {
    if (!isAdmin) {
      dispatch(
        showGlobalToast({
          message: 'Chỉ tài khoản Admin mới có quyền xóa phiếu lắp đặt!',
          type: messageToastType_const.warning
        })
      );
      return;
    }
    const ticketId = ticket.rawId || ticket.id;
    if (!selectedTicketIds.includes(ticketId)) {
      setSelectedTicketIds([ticketId]);
    }
    setOpenDeleteConfirm(true);
    handleMenuClose();
  };

  const handleOpenBulkDeleteConfirm = () => {
    if (!isAdmin) {
      dispatch(
        showGlobalToast({
          message: 'Chỉ tài khoản Admin mới có quyền xóa phiếu lắp đặt!',
          type: messageToastType_const.warning
        })
      );
      return;
    }
    if (selectedTicketIds.length === 0) return;
    setOpenDeleteConfirm(true);
  };

  const confirmDeleteTickets = async () => {
    if (!selectedTicketIds || selectedTicketIds.length === 0) return;
    try {
      await axiosInterceptor.delete('/installations', {
        data: { ids: selectedTicketIds }
      });
      dispatch(
        showGlobalToast({
          message: `Đã xóa thành công ${selectedTicketIds.length} phiếu lắp đặt!`,
          type: messageToastType_const.success
        })
      );
      setSelectedTicketIds([]);
      fetchListInstallations();
    } catch (err) {
      console.error('Delete tickets error:', err);
      dispatch(
        showGlobalToast({
          message: 'Xóa phiếu thất bại: ' + (err.response?.data?.message || err.message),
          type: messageToastType_const.error
        })
      );
    } finally {
      setOpenDeleteConfirm(false);
    }
  };

  // Tổng phí lắp đặt và tổng tiền mặt KTV thu hộ (Loại bỏ các phiếu Kế toán đã duyệt đối soát status === 4 hoặc RECONCILED)
  const summaryTotals = useMemo(() => {
    const listToCalc = activeDisplayList || installations || [];

    const feeSum = listToCalc.reduce(
      (acc, curr) => (curr.status !== -1 ? acc + (Number(curr.fee) || 0) : acc),
      0
    );

    const cashSum = listToCalc.reduce((acc, curr) => {
      const isCancelled = curr.status === -1;
      const isReconciled = curr.status === 4 || curr.paymentStatus === 'RECONCILED';
      if (!isCancelled && !isReconciled) {
        return acc + (Number(curr.cashCollectedByTech) || 0);
      }
      return acc;
    }, 0);

    return { feeSum, cashSum };
  }, [installations, activeDisplayList]);

  return (
    <Box className={classes.root}>
      {/* Hidden File Input cho Upload ảnh Nghiệm thu */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept="image/*"
        multiple
        onChange={handleFileChange}
      />

      {/* 📊 1. SUMMARY CHIPS (5 Trạng thái chuẩn CMS) */}
      <Box className={classes.summaryRow}>
        {Object.entries(INSTALLATION_STATUS).map(([key, cfg]) => {
          const numKey = Number(key);
          const isActive = filterStatus === numKey || filterStatus === key;
          return (
            <Box
              key={key}
              onClick={() => {
                setFilterStatus(isActive ? 'all' : numKey);
                setPage(0);
              }}
              className={classes.summaryChip}
              style={{
                borderColor: cfg.color,
                background: isActive ? cfg.color : cfg.bg,
                cursor: 'pointer',
                transform: isActive ? 'translateY(-2px)' : 'none',
                boxShadow: isActive ? `0 4px 12px ${cfg.color}33` : 'none',
                transition: 'all 0.2s'
              }}
            >
              <span
                className={classes.summaryDot}
                style={{ background: isActive ? '#fff' : cfg.color }}
              />
              <Typography
                style={{
                  fontSize: 12,
                  color: isActive ? '#fff' : cfg.color,
                  fontWeight: 700
                }}
              >
                {cfg.label}: {countsByStatus[key] || 0}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* 🔍 2. TOOLBAR & FILTERS SECTION (Đồng bộ chuẩn Quản trị bán hàng) */}
      <Card className={classes.filterCard} style={{ padding: '12px 16px' }}>
        <Box display="flex" alignItems="center" flexWrap="wrap" gridGap={12} style={{ width: '100%' }}>
          {/* Ô Tìm kiếm */}
          <TextField
            id="search-installations"
            className={classes.searchInput}
            placeholder="Tìm theo tên, SĐT, mã phiếu..."
            variant="outlined"
            size="small"
            value={search}
            onChange={e => {
              setSearch(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" style={{ color: '#94a3b8' }} />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearch('');
                      setPage(0);
                    }}
                  >
                    <CloseIcon style={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ) : null
            }}
          />

          {/* Lọc Từ ngày */}
          <TextField
            label="Từ ngày"
            type="date"
            placeholder="Từ ngày"
            variant="outlined"
            size="small"
            value={fromDate}
            onChange={e => {
              setFromDate(e.target.value);
              setPage(0);
            }}
            className={classes.filterSelect}
            InputLabelProps={{
              shrink: true,
              style: {
                backgroundColor: 'white',
                padding: '0 4px',
                marginLeft: -4
              }
            }}
            InputProps={{
              endAdornment: fromDate ? (
                <InputAdornment
                  position="end"
                  style={{ position: 'absolute', right: 28 }}
                >
                  <IconButton
                    size="small"
                    onClick={() => {
                      setFromDate('');
                      setPage(0);
                    }}
                    style={{ padding: 2 }}
                  >
                    <CloseIcon style={{ fontSize: 14 }} />
                  </IconButton>
                </InputAdornment>
              ) : null
            }}
          />

          {/* Lọc Đến ngày */}
          <TextField
            label="Đến ngày"
            type="date"
            placeholder="Đến ngày"
            variant="outlined"
            size="small"
            value={toDate}
            onChange={e => {
              setToDate(e.target.value);
              setPage(0);
            }}
            className={classes.filterSelect}
            InputLabelProps={{
              shrink: true,
              style: {
                backgroundColor: 'white',
                padding: '0 4px',
                marginLeft: -4
              }
            }}
            InputProps={{
              endAdornment: toDate ? (
                <InputAdornment
                  position="end"
                  style={{ position: 'absolute', right: 28 }}
                >
                  <IconButton
                    size="small"
                    onClick={() => {
                      setToDate('');
                      setPage(0);
                    }}
                    style={{ padding: 2 }}
                  >
                    <CloseIcon style={{ fontSize: 14 }} />
                  </IconButton>
                </InputAdornment>
              ) : null
            }}
          />

          {/* Nút Lọc (Popover chứa bộ lọc bổ sung) */}
          <Button
            variant="outlined"
            size="small"
            onClick={handleFilterClick}
            startIcon={<FilterListIcon />}
            style={{
              height: 40,
              borderRadius: 8,
              textTransform: 'none',
              fontWeight: 600,
              color: activeFilterCount > 0 ? '#D9042F' : '#666',
              borderColor: activeFilterCount > 0 ? '#D9042F' : '#e0e3ef',
              backgroundColor: activeFilterCount > 0 ? '#fff1f0' : 'white'
            }}
          >
            Lọc {activeFilterCount > 0 ? `(${activeFilterCount})` : ''}
          </Button>

          {/* Popover cho Bộ lọc bổ sung (Đối tượng lắp, Kỹ thuật viên) */}
          <Popover
            open={Boolean(filterAnchorEl)}
            anchorEl={filterAnchorEl}
            onClose={handleFilterClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left'
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left'
            }}
            PaperProps={{
              style: {
                padding: 20,
                width: 300,
                display: 'flex',
                flexDirection: 'column',
                gap: 16,
                borderRadius: 12,
                marginTop: 8,
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
              }
            }}
          >
            <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
              Bộ lọc bổ sung
            </Typography>

            <FormControl variant="outlined" size="small" fullWidth>
              <InputLabel shrink style={{ backgroundColor: 'white', padding: '0 4px', marginLeft: -4 }}>
                Đối tượng lắp
              </InputLabel>
              <Select
                value={filterTarget}
                onChange={e => {
                  setFilterTarget(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="all">Tất cả đối tượng</MenuItem>
                <MenuItem value="VNT">Lắp cho VMAKE</MenuItem>
                <MenuItem value="AGENCY">Lắp cho Đại lý</MenuItem>
              </Select>
            </FormControl>

            <FormControl variant="outlined" size="small" fullWidth>
              <InputLabel shrink style={{ backgroundColor: 'white', padding: '0 4px', marginLeft: -4 }}>
                Kỹ thuật viên
              </InputLabel>
              <Select
                value={filterTech}
                onChange={e => {
                  setFilterTech(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="all">Tất cả KTV</MenuItem>
                <MenuItem value="Chưa nhận">Chưa nhận phiếu</MenuItem>
                {techOptions.map(name => (
                  <MenuItem key={name} value={name}>{name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={handleFilterClose}
              style={{ textTransform: 'none', fontWeight: 600, borderRadius: 8 }}
            >
              Áp dụng
            </Button>
          </Popover>

          {/* Nút Reset bộ lọc */}
          {(search || filterStatus !== 'all' || filterTech !== 'all' || filterTarget !== 'all' || fromDate || toDate) && (
            <Tooltip title="Xóa tất cả bộ lọc">
              <IconButton
                size="small"
                onClick={() => {
                  setSearch('');
                  setFilterStatus('all');
                  setFilterTech('all');
                  setFilterTarget('all');
                  setFromDate('');
                  setToDate('');
                  setPage(0);
                }}
                style={{
                  color: '#D9042F',
                  border: '1px solid #D9042F33',
                  borderRadius: 8,
                  padding: 8,
                  height: 40,
                  width: 40
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {/* Nút Xóa phiếu hàng loạt dành cho Admin */}
          {isAdmin && selectedTicketIds.length > 0 && (
            <Button
              variant="contained"
              size="small"
              startIcon={<DeleteIcon fontSize="small" />}
              onClick={handleOpenBulkDeleteConfirm}
              style={{
                borderRadius: 8,
                textTransform: 'none',
                fontWeight: 700,
                height: 40,
                padding: '8px 16px',
                backgroundColor: '#d32f2f',
                color: '#fff',
                boxShadow: 'none',
                whiteSpace: 'nowrap'
              }}
            >
              Xóa ({selectedTicketIds.length}) phiếu đã chọn
            </Button>
          )}

          {/* Nút Tạo phiếu lắp đặt mới (nằm góc phải) */}
          {canCreateTicket && (
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon fontSize="small" />}
              onClick={handleOpenAddModal}
              style={{
                borderRadius: 8,
                textTransform: 'none',
                fontWeight: 700,
                height: 40,
                padding: '8px 16px',
                backgroundColor: '#d32f2f',
                color: '#fff',
                boxShadow: 'none',
                whiteSpace: 'nowrap',
                marginLeft: 'auto'
              }}
            >
              Tạo phiếu lắp đặt
            </Button>
          )}
        </Box>
      </Card>

      {/* 📋 3. BẢNG DANH SÁCH PHIẾU LẮP ĐẶT (Tối giản, nhã nhặn) */}
      <Card className={classes.tableCard}>
        <Box
          ref={scrollContainerRef}
          onMouseDown={onMouseDown}
          onMouseLeave={onMouseLeave}
          onMouseUp={onMouseUp}
          onMouseMove={onMouseMove}
          onScroll={handleScroll}
          style={{
            cursor: isDown ? 'grabbing' : 'grab',
            overflowX: 'auto',
            overflowY: 'hidden',
            userSelect: isDown ? 'none' : 'auto',
            width: '100%'
          }}
        >
          <Table style={{ borderCollapse: 'separate', borderSpacing: 0, minWidth: 1400 }}>
            <TableHead>
              <TableRow>
                {isAdmin && (
                  <StyledTableCell
                    className={classes.stickyHeader}
                    style={{ left: 0, width: 45, minWidth: 45, padding: '0 4px', textAlign: 'center' }}
                  >
                    <Checkbox
                      size="small"
                      checked={isAllSelected}
                      indeterminate={isSomeSelected}
                      onChange={handleSelectAll}
                      style={{ color: '#d32f2f', padding: 4 }}
                    />
                  </StyledTableCell>
                )}
                <StyledTableCell className={classes.stickyHeader} style={{ left: isAdmin ? 45 : 0, width: 100, minWidth: 100 }}>
                  Thời gian
                </StyledTableCell>
                <StyledTableCell className={classes.stickyHeader} style={{ left: isAdmin ? 145 : 100, width: 130, minWidth: 130 }}>
                  Mã phiếu & Đơn
                </StyledTableCell>
                <StyledTableCell
                  className={classes.stickyHeader}
                  style={{
                    left: isAdmin ? 275 : 230,
                    width: 130,
                    minWidth: 130,
                    borderRight: isScrolled ? '1px solid rgba(0,0,0,0.1)' : 'none',
                    boxShadow: isScrolled ? '4px 0 8px rgba(0,0,0,0.06)' : 'none',
                    transition: 'border-right 0.2s, box-shadow 0.2s'
                  }}
                >
                  Nhân viên Sale
                </StyledTableCell>
                <StyledTableCell style={{ minWidth: 220 }}>Khách hàng & Địa chỉ</StyledTableCell>
                <StyledTableCell style={{ minWidth: 160 }}>Phương tiện</StyledTableCell>
                <StyledTableCell style={{ minWidth: 170 }}>Mã thiết bị</StyledTableCell>
                <StyledTableCell style={{ minWidth: 150 }}>Số IMEI</StyledTableCell>
                <StyledTableCell style={{ minWidth: 200 }}>Ghi chú thi công</StyledTableCell>
                <StyledTableCell style={{ minWidth: 150 }}>KTV Phụ trách</StyledTableCell>
                <StyledTableCell style={{ minWidth: 160 }}>Thanh toán & Tiền mặt</StyledTableCell>
                <StyledTableCell style={{ minWidth: 140 }} align="center">Trạng thái phiếu</StyledTableCell>
                <StyledTableCell style={{ minWidth: 150 }} align="center">Duyệt đối soát</StyledTableCell>
                <StyledTableCell style={{ minWidth: 110 }} align="center">Nghiệm thu</StyledTableCell>
                <StyledTableCell style={{ minWidth: 60 }} align="center">Thao tác</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <StyledTableCell colSpan={14} align="center" style={{ padding: 40 }}>
                    <Box display="flex" justifyContent="center" alignItems="center" gridGap={10}>
                      <CircularProgress size={24} style={{ color: '#2563eb' }} />
                      <Typography variant="body2" style={{ color: '#64748b', fontWeight: 600 }}>
                        Đang tải danh sách phiếu lắp đặt...
                      </Typography>
                    </Box>
                  </StyledTableCell>
                </TableRow>
              ) : activeDisplayList.length === 0 ? (
                <TableRow>
                  <StyledTableCell colSpan={14} align="center" style={{ padding: 40, color: '#64748b' }}>
                    <Typography variant="body1" color="textSecondary">
                      Không tìm thấy phiếu lắp đặt nào phù hợp...
                    </Typography>
                  </StyledTableCell>
                </TableRow>
              ) : (
                activeDisplayList.map(row => {
                  const st = INSTALLATION_STATUS[row.status] || INSTALLATION_STATUS[0];
                  const paySt = PAYMENT_STATUS_CONFIG[row.paymentStatus] || PAYMENT_STATUS_CONFIG.UNPAID;
                  const dateParts = (row.createdAt || '10/09/2026 09:00').split(' ');

                  return (
                    <TableRow key={row.id} hover className={classes.tableRow}>
                      {isAdmin && (
                        <StyledTableCell
                          className={classes.stickyColFirst}
                          style={{ left: 0, width: 45, minWidth: 45, padding: '0 4px', textAlign: 'center' }}
                        >
                          <Checkbox
                            size="small"
                            checked={selectedTicketIds.includes(row.rawId || row.id)}
                            onChange={e => handleSelectRow(e, row.rawId || row.id)}
                            style={{ color: '#d32f2f', padding: 4 }}
                          />
                        </StyledTableCell>
                      )}
                      {/* 1. Thời gian tạo */}
                      <StyledTableCell
                        className={classes.stickyColFirst}
                        style={{
                          left: isAdmin ? 45 : 0,
                          width: 100,
                          minWidth: 100,
                          boxShadow: isScrolled ? 'none' : 'none'
                        }}
                      >
                        <Typography style={{ fontSize: 12, fontWeight: 700, color: st.color }}>
                          {dateParts[0]}
                        </Typography>
                        <Typography style={{ fontSize: 11, color: st.color, opacity: 0.85, fontWeight: 600 }}>
                          {dateParts[1] || ''}
                        </Typography>
                      </StyledTableCell>

                      {/* 2. Mã phiếu */}
                      <StyledTableCell
                        className={classes.stickyColSecond}
                        style={{
                          left: isAdmin ? 145 : 100,
                          width: 130,
                          minWidth: 130
                        }}
                      >
                        <Typography style={{ fontSize: 13, fontWeight: 700, color: '#d32f2f', lineHeight: 1.4 }}>
                          {row.id}
                        </Typography>
                        {row.orderCode && (
                          <Typography style={{ fontSize: 11, color: '#2563eb', fontWeight: 600, lineHeight: 1.4 }}>
                            Đơn: {row.orderCode}
                          </Typography>
                        )}
                        <Typography style={{ fontSize: 11, fontWeight: 600, color: (row.targetType === 'VNT' || row.targetType === 'VMAKE') ? '#059669' : '#d97706', lineHeight: 1.4 }}>
                          {(row.targetType === 'VNT' || row.targetType === 'VMAKE') ? 'Lắp cho VMAKE' : 'Lắp cho Đại lý'}
                        </Typography>
                      </StyledTableCell>

                      {/* 3. Nhân viên Sale */}
                      <StyledTableCell
                        className={classes.stickyColThird}
                        style={{
                          left: isAdmin ? 275 : 230,
                          width: 130,
                          minWidth: 130,
                          borderRight: isScrolled ? '1px solid rgba(0,0,0,0.1)' : 'none',
                          boxShadow: isScrolled ? '4px 0 8px rgba(0,0,0,0.06)' : 'none',
                          transition: 'border-right 0.2s, box-shadow 0.2s'
                        }}
                      >
                        <Typography style={{ fontSize: 12, fontWeight: 600, color: '#1e293b', lineHeight: 1.4 }}>
                          {row.createdBySale || '--'}
                        </Typography>
                      </StyledTableCell>

                      {/* 3. Khách hàng & Địa chỉ */}
                      <StyledTableCell style={{ minWidth: 220 }}>
                        <Typography style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', lineHeight: 1.4 }}>
                          {row.customerName}
                        </Typography>
                        <Typography style={{ fontSize: 12, color: '#64748b', lineHeight: 1.4 }}>
                          {row.customerPhone}
                        </Typography>
                        <Typography
                          style={{
                            fontSize: 12,
                            color: '#64748b',
                            whiteSpace: 'normal',
                            lineHeight: 1.3,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                          }}
                        >
                          {row.address}
                        </Typography>
                      </StyledTableCell>

                      {/* 4. Phương tiện */}
                      <StyledTableCell style={{ minWidth: 160 }}>
                        {row.licensePlate ? (
                          <>
                            <Typography style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', lineHeight: 1.4 }}>
                              [{row.licensePlate}]
                            </Typography>
                            {row.vehicleType && (
                              <Typography style={{ fontSize: 12, color: '#64748b', lineHeight: 1.4 }}>
                                {row.vehicleType}
                              </Typography>
                            )}
                          </>
                        ) : (
                          <Typography style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', lineHeight: 1.4 }}>
                            {row.vehicleType || '--'}
                          </Typography>
                        )}
                      </StyledTableCell>

                      {/* 5. Mã thiết bị */}
                      <StyledTableCell style={{ minWidth: 170 }}>
                        {row.deviceCode ? (
                          <Typography style={{ fontSize: 12, color: '#2563eb', fontWeight: 600, lineHeight: 1.4 }}>
                            {row.deviceCode}
                          </Typography>
                        ) : !row.hasFuelSensor && !row.hasTempSensor ? (
                          <Typography style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.4 }}>
                            --
                          </Typography>
                        ) : null}
                        {(row.hasFuelSensor || row.hasTempSensor) && (
                          <Typography style={{ fontSize: 11, color: '#b45309', fontWeight: 600, lineHeight: 1.4 }}>
                            {[row.hasFuelSensor ? 'CB Xăng dầu' : '', row.hasTempSensor ? 'CB Nhiệt độ' : ''].filter(Boolean).join(' • ')}
                          </Typography>
                        )}
                      </StyledTableCell>

                      {/* 6. Số IMEI */}
                      <StyledTableCell style={{ minWidth: 150 }}>
                        <Typography style={{ fontSize: 12, color: row.serialImei && row.serialImei !== 'Chưa gán' && row.serialImei !== '--' ? '#0f172a' : '#94a3b8', fontFamily: 'monospace', fontWeight: 600, lineHeight: 1.4 }}>
                          {!row.serialImei || row.serialImei === 'Chưa gán' ? '--' : row.serialImei}
                        </Typography>
                      </StyledTableCell>

                      {/* 5. Ghi chú thi công */}
                      <StyledTableCell style={{ minWidth: 200 }}>
                        <Tooltip title={row.notes || ''} placement="top-start">
                          <Typography
                            style={{
                              fontSize: 12,
                              color: '#475569',
                              whiteSpace: 'normal',
                              lineHeight: 1.4,
                              display: '-webkit-box',
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden'
                            }}
                          >
                            {row.notes || '--'}
                          </Typography>
                        </Tooltip>
                      </StyledTableCell>

                      {/* 6. KTV Phụ trách / NÚT NHẬN PHIẾU */}
                      <StyledTableCell style={{ minWidth: 140 }}>
                        {row.status === 0 ? (
                          <Tooltip title={canClaimTicket ? "Bấm để nhận phiếu thi công ngay" : "Chỉ tài khoản CSKH hoặc KTV (huydv) mới được nhận"}>
                            <span>
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => handleClaimTicket(row)}
                                disabled={!canClaimTicket}
                                style={{
                                  textTransform: 'none',
                                  fontSize: 12,
                                  fontWeight: 700,
                                  height: 26,
                                  borderRadius: 6,
                                  backgroundColor: '#d32f2f',
                                  color: '#ffffff',
                                  boxShadow: 'none'
                                }}
                              >
                                Nhận phiếu
                              </Button>
                            </span>
                          </Tooltip>
                        ) : (
                          <>
                            <Typography style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', lineHeight: 1.4 }}>
                              {row.technician}
                            </Typography>
                            <Typography style={{ fontSize: 12, color: '#64748b', lineHeight: 1.4 }}>
                              {row.techPhone}
                            </Typography>
                            {row.status === 1 && (
                              <Tooltip title="Bấm để hủy nhận phiếu (trả lại trạng thái Chờ nhận)">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => handleUnclaimTicket(row)}
                                  disabled={!canClaimTicket}
                                  style={{
                                    textTransform: 'none',
                                    fontSize: 11,
                                    height: 22,
                                    marginTop: 4,
                                    padding: '0 6px',
                                    color: '#d32f2f',
                                    borderColor: '#d32f2f55',
                                    backgroundColor: '#fff1f0'
                                  }}
                                >
                                  Hủy nhận đơn
                                </Button>
                              </Tooltip>
                            )}
                          </>
                        )}
                      </StyledTableCell>

                      {/* 7. Thanh toán & Thu tiền mặt */}
                      <StyledTableCell style={{ minWidth: 160 }}>
                        <Typography style={{ fontSize: 11, color: row.paymentMethod === 'CASH_KTV' ? '#166534' : '#1e40af', fontWeight: 600, lineHeight: 1.4 }}>
                          {row.paymentMethod === 'CASH_KTV' ? 'Tiền mặt (KTV thu hộ)' : 'Khách CK cho Sale'}
                        </Typography>
                        <Typography style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', lineHeight: 1.4 }}>
                          Phí: {formatMoney(row.fee || 0)}
                        </Typography>
                        {row.paymentMethod === 'CASH_KTV' && (
                          <Typography style={{ fontSize: 11, color: '#166534', fontWeight: 600, lineHeight: 1.4 }}>
                            Tiền thu: {formatMoney(row.cashCollectedByTech || row.fee || 0)}
                          </Typography>
                        )}
                      </StyledTableCell>

                      {/* 8. Trạng thái phiếu */}
                      <StyledTableCell style={{ minWidth: 140 }} align="center">
                        <span
                          style={{
                            backgroundColor: st.bg,
                            color: st.color,
                            fontWeight: 700,
                            fontSize: 11,
                            border: `1px solid ${st.color}33`,
                            padding: '3px 10px',
                            borderRadius: 12,
                            display: 'inline-block',
                            lineHeight: 1.2
                          }}
                        >
                          {st.label}
                        </span>
                      </StyledTableCell>

                      {/* 9. Duyệt đối soát */}
                      <StyledTableCell style={{ minWidth: 150 }} align="center">
                        {row.status === 1 ? (
                          <Tooltip title="KTV bấm báo hoàn thành thi công và gửi đối soát tiền">
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleSubmitReconciliation(row)}
                              style={{
                                textTransform: 'none',
                                fontSize: 11,
                                height: 26,
                                color: '#2563eb',
                                borderColor: '#bfdbfe',
                                backgroundColor: '#eff6ff',
                                fontWeight: 700
                              }}
                            >
                              Báo hoàn thành
                            </Button>
                          </Tooltip>
                        ) : row.status === 2 ? (
                          <Tooltip title={canReconcile ? "Kế toán kiểm tra tiền & duyệt hoàn thành phiếu" : "Chỉ Kế toán hoặc Admin mới có quyền duyệt đối soát"}>
                            <span>
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => handleReconcileComplete(row)}
                                disabled={!canReconcile}
                                style={{
                                  textTransform: 'none',
                                  fontSize: 11,
                                  fontWeight: 700,
                                  height: 26,
                                  color: '#ffffff',
                                  backgroundColor: canReconcile ? '#16a34a' : '#94a3b8',
                                  boxShadow: 'none'
                                }}
                              >
                                Duyệt đối soát
                              </Button>
                            </span>
                          </Tooltip>
                        ) : row.status === 4 ? (
                          <Box display="flex" alignItems="center" justifyContent="center" style={{ gap: 4 }}>
                            <CheckCircleIcon style={{ color: '#16a34a', fontSize: 18 }} />
                            <Typography style={{ fontSize: 11, fontWeight: 700, color: '#16a34a' }}>
                              Đã đối soát
                            </Typography>
                          </Box>
                        ) : (
                          <Typography style={{ fontSize: 12, color: '#94a3b8' }}>--</Typography>
                        )}
                      </StyledTableCell>

                      {/* 10. Nghiệm thu */}
                      <StyledTableCell style={{ minWidth: 110 }} align="center">
                        {row.images && row.images.length > 0 ? (
                          <Box display="flex" alignItems="center" justifyContent="center" style={{ gap: 6 }}>
                            <Tooltip title={`Xem ${row.images.length} ảnh nghiệm thu`}>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setSelectedTicket(row);
                                  setOpenGalleryModal(true);
                                }}
                                style={{ backgroundColor: '#fff1f0', color: '#d32f2f', padding: 4 }}
                              >
                                <Badge badgeContent={row.images.length} color="secondary">
                                  <PhotoLibraryIcon fontSize="small" />
                                </Badge>
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Thêm ảnh nghiệm thu">
                              <IconButton
                                size="small"
                                onClick={() => handleTriggerUpload(row)}
                                style={{ backgroundColor: '#f1f5f9', color: '#475569', padding: 4 }}
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ) : (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleTriggerUpload(row)}
                            style={{
                              fontSize: 11,
                              textTransform: 'none',
                              height: 26,
                              borderRadius: 6,
                              borderColor: '#cbd5e1',
                              color: '#475569',
                              backgroundColor: '#f8fafc'
                            }}
                          >
                            + Thêm ảnh
                          </Button>
                        )}
                      </StyledTableCell>

                      {/* 11. Thao tác */}
                      <StyledTableCell style={{ minWidth: 60 }} align="center">
                        <IconButton size="small" onClick={e => handleMenuOpen(e, row)}>
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </StyledTableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Box>

        {/* 📄 PHẦN PHÂN TRANG */}
        {activeDisplayList && activeDisplayList.length > 0 && (
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            style={{ width: '100%' }}
            p={2}
            borderTop="1px solid #e2e8f0"
          >
            <Typography style={{ fontSize: 13, color: '#45485E', fontWeight: 600 }}>
              Tổng số phiếu: <strong style={{ color: '#d32f2f' }}>{totalCount}</strong> | Tổng phí: <strong style={{ color: '#d32f2f' }}>{formatMoney(summaryTotals.feeSum)}</strong> | Tiền KTV thu mặt hộ: <strong style={{ color: '#16a34a' }}>{formatMoney(summaryTotals.cashSum)}</strong>
            </Typography>

            <Box style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
              <Pagination
                count={Math.ceil((totalCount || 0) / rowsPerPage) || 1}
                color="primary"
                size="small"
                page={page + 1}
                onChange={(_, p) => setPage(p - 1)}
                siblingCount={1}
                boundaryCount={2}
                showFirstButton
                showLastButton
              />
            </Box>
          </Box>
        )}
      </Card>

      {/* 📌 MENU THAO TÁC (ACTIONS MENU) */}
      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{ style: { borderRadius: 8, minWidth: 180 } }}
      >
        <MenuItem onClick={() => handleEditTicket(selectedTicket)}>
          <EditIcon style={{ fontSize: 18, marginRight: 8, color: '#d32f2f' }} /> Chỉnh sửa phiếu
        </MenuItem>
        {selectedTicket?.status === 1 && (
          <MenuItem onClick={() => { handleUnclaimTicket(selectedTicket); handleMenuClose(); }}>
            <ClearIcon style={{ fontSize: 18, marginRight: 8, color: '#d32f2f' }} /> Hủy nhận đơn (Nhả phiếu)
          </MenuItem>
        )}
        <MenuItem onClick={() => handleCancelTicket(selectedTicket)}>
          <CancelIcon style={{ fontSize: 18, marginRight: 8, color: '#666' }} /> Hủy / Hoãn phiếu
        </MenuItem>
        {isAdmin && (
          <MenuItem onClick={() => handleOpenSingleDeleteConfirm(selectedTicket)}>
            <DeleteIcon style={{ fontSize: 18, marginRight: 8, color: '#d32f2f' }} /> Xóa phiếu lắp đặt
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={() => { setOpenPrintModal(true); handleMenuClose(); }}>
          <PrintIcon style={{ fontSize: 18, marginRight: 8, color: '#45485E' }} /> In Biên bản nghiệm thu
        </MenuItem>
      </Menu>

      {/* ❓ MODAL XÁC NHẬN XÓA PHIẾU LẮP ĐẶT (Chỉ Admin) */}
      <Dialog
        open={openDeleteConfirm}
        onClose={() => setOpenDeleteConfirm(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ style: { borderRadius: 12 } }}
      >
        <DialogTitle style={{ borderBottom: '1px solid #e0e3ef', padding: '16px 24px' }}>
          <Typography variant="h6" style={{ fontWeight: 700, color: '#d32f2f' }}>
            Xác nhận xóa phiếu lắp đặt
          </Typography>
        </DialogTitle>
        <DialogContent style={{ padding: '20px 24px' }}>
          <Typography variant="body1" style={{ color: '#1a1f36' }}>
            Bạn có chắc chắn muốn xóa vĩnh viễn <strong>{selectedTicketIds.length}</strong> phiếu lắp đặt đã chọn?
          </Typography>
          <Typography variant="caption" style={{ color: '#64748b', marginTop: 8, display: 'block' }}>
            Hành động này sẽ xóa các phiếu được chọn khỏi hệ thống và không thể hoàn tác.
          </Typography>
        </DialogContent>
        <DialogActions style={{ padding: '12px 24px', borderTop: '1px solid #f1f5f9' }}>
          <Button
            onClick={() => setOpenDeleteConfirm(false)}
            style={{ textTransform: 'none', fontWeight: 600, color: '#64748b' }}
          >
            Hủy
          </Button>
          <Button
            variant="contained"
            onClick={confirmDeleteTickets}
            style={{
              textTransform: 'none',
              fontWeight: 700,
              backgroundColor: '#d32f2f',
              color: '#ffffff',
              borderRadius: 8,
              boxShadow: 'none'
            }}
          >
            Xóa {selectedTicketIds.length} phiếu
          </Button>
        </DialogActions>
      </Dialog>

      {/* 📝 4. MODAL TẠO / CHỈNH SỬA PHIẾU LẮP ĐẶT (Động theo loại thiết bị & phí) */}
      <Dialog
        open={openAddModal}
        onClose={() => setOpenAddModal(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ style: { borderRadius: 12 } }}
      >
        <DialogTitle style={{ borderBottom: '1px solid #e0e3ef', padding: '16px 24px' }}>
          <Typography variant="h5" style={{ fontWeight: 700, color: '#1a1f36' }}>
            {isEditing ? `Chỉnh sửa phiếu: ${form.id}` : '+ Lập phiếu lắp đặt'}
          </Typography>
        </DialogTitle>
        <DialogContent style={{ padding: '20px 24px' }}>
          {/* Nhóm 1: Thông tin đối tượng & Khách hàng */}
          <Box mb={3}>
            <Typography variant="subtitle1" style={{ fontWeight: 700, color: '#d32f2f', marginBottom: 12 }}>
              1. THÔNG TIN KHÁCH HÀNG & ĐỐI TƯỢNG LẮP ĐẶT
            </Typography>

            <Tabs
              value={tabForm}
              onChange={(_, v) => {
                setTabForm(v);
                setSelectedUser(null);
                setFetchType(v === 0 ? 'account' : 'phone');
                setForm(prev => ({
                  ...prev,
                  targetType: v === 0 ? 'AGENCY' : 'VNT'
                }));
              }}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
              style={{ marginBottom: 16, borderBottom: '1px solid #eee' }}
            >
              <Tab
                label="Đại lý"
                disabled={isEditing}
                style={{ fontWeight: 700, textTransform: 'none' }}
              />
              <Tab
                label="Khách lẻ"
                disabled={isEditing}
                style={{ fontWeight: 700, textTransform: 'none' }}
              />
            </Tabs>

            <Box display="flex" flexDirection="column" style={{ marginBottom: 16 }}>
              <Box display="flex" style={{ gap: 8, marginBottom: 8 }}>
                {tabForm === 0 && (
                  <FormControl
                    variant="outlined"
                    size="small"
                    style={{ minWidth: 140 }}
                  >
                    <Select
                      value={fetchType}
                      disabled={isEditing}
                      onChange={e => setFetchType(e.target.value)}
                    >
                      <MenuItem value="account">Tài khoản</MenuItem>
                      <MenuItem value="phone">Số điện thoại</MenuItem>
                    </Select>
                  </FormControl>
                )}
                <FormControl fullWidth variant="outlined" size="small">
                  <Autocomplete
                    fullWidth
                    disabled={isEditing}
                    size="small"
                    openOnFocus={tabForm === 0}
                    options={tabForm === 0 ? listAgencies || [] : listUsers || []}
                    loading={suggestionLoading}
                    filterOptions={x => x}
                    value={selectedUser}
                    isOptionEqualToValue={(option, value) =>
                      (option.id || option.agency_id || option.user_id) ===
                      (value.id || value.agency_id || value.user_id)
                    }
                    getOptionLabel={option => {
                      if (!option) return '';
                      if (tabForm === 0) {
                        return fetchType === 'account'
                          ? option.username || option.name || ''
                          : option.leader?.phone || option.phone || '';
                      }
                      return option.phone || option.username || option.full_name || '';
                    }}
                    onInputChange={(event, newInputValue) => {
                      handleAutocompleteSearch(newInputValue);
                      if (!newInputValue) setSelectedUser(null);
                    }}
                    onChange={(event, newValue) => {
                      handleFetchUserInfo(newValue);
                    }}
                    noOptionsText={
                      suggestionLoading
                        ? 'Đang tìm kiếm...'
                        : 'Không tìm thấy kết quả'
                    }
                    renderOption={option => (
                      <Box>
                        <Typography style={{ fontWeight: 600, fontSize: 13 }}>
                          {tabForm === 0
                            ? option.name
                            : option.full_name || option.fullname || option.username}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {tabForm === 0
                            ? `${option.username || ''}${option.username ? ' - ' : ''}${option.leader?.phone || option.phone || ''}`
                            : `${option.username || ''}${option.username ? ' - ' : ''}${option.phone || ''}`}
                        </Typography>
                      </Box>
                    )}
                    renderInput={params => (
                      <TextField
                        {...params}
                        placeholder={
                          tabForm === 0
                            ? fetchType === 'account'
                              ? isEditing ? 'Tài khoản đại lý' : 'Tìm tài khoản đại lý'
                              : isEditing ? 'Số điện thoại đại lý' : 'Tìm số điện thoại đại lý'
                            : isEditing ? 'Số điện thoại / Tên khách hàng' : 'Tìm số điện thoại / Tên khách hàng'
                        }
                        variant="outlined"
                      />
                    )}
                  />
                </FormControl>
              </Box>
            </Box>

            <Grid container spacing={2} style={{ marginBottom: 16 }}>
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="subtitle2"
                  style={{ fontSize: 13, color: '#555', marginBottom: 4 }}
                >
                  {tabForm === 0 ? 'Tên đại lý/công ty' : 'Tên khách hàng'}
                </Typography>
                <TextField
                  fullWidth
                  disabled
                  value={form.customerName || ''}
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="subtitle2"
                  style={{ fontSize: 13, color: '#555', marginBottom: 4 }}
                >
                  Số điện thoại
                </Typography>
                <TextField
                  fullWidth
                  disabled
                  value={form.customerPhone || ''}
                  variant="outlined"
                  size="small"
                />
              </Grid>
            </Grid>

            <Box mb={2}>
              <Typography
                variant="subtitle2"
                style={{ fontSize: 13, color: '#555', marginBottom: 4 }}
              >
                Địa chỉ
              </Typography>
              <TextField
                fullWidth
                disabled
                value={form.address || ''}
                variant="outlined"
                size="small"
              />
            </Box>
          </Box>

          <Divider style={{ margin: '16px 0' }} />

          {/* Nhóm 2: Thông tin phương tiện, Mã thiết bị & Cảm biến */}
          <Box mb={3}>
            <Typography variant="subtitle1" style={{ fontWeight: 700, color: '#d32f2f', marginBottom: 12 }}>
              2. THÔNG TIN PHƯƠNG TIỆN & THIẾT BỊ / CẢM BIẾN LẮP ĐẶT
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" style={{ fontSize: 13, color: '#555', marginBottom: 4 }}>
                  Biển số xe (nếu có)
                </Typography>
                <TextField
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={form.licensePlate}
                  onChange={e => setForm({ ...form, licensePlate: e.target.value })}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" style={{ fontSize: 13, color: '#555', marginBottom: 4 }}>
                  Loại xe / Dòng xe
                </Typography>
                <TextField
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={form.vehicleType}
                  onChange={e => setForm({ ...form, vehicleType: e.target.value })}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" style={{ fontSize: 13, color: '#555', marginBottom: 4 }}>
                  Mã Thiết Bị (Device Code)
                </Typography>
                <FormControl variant="outlined" size="small" fullWidth>
                  <Select
                    value={form.deviceCode || ''}
                    onChange={e => handleSelectDeviceCode(e.target.value)}
                  >
                    {availableDeviceOptions.map(dev => (
                      <MenuItem key={dev.id || dev.code} value={dev.code}>
                        {dev.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Tùy chọn Cảm biến đính kèm */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" style={{ fontSize: 13, color: '#555', marginBottom: 4 }}>
                  Lắp đặt Cảm biến đính kèm
                </Typography>
                <Box display="flex" style={{ gap: 24, backgroundColor: '#f8fafc', padding: '8px 16px', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(form.hasFuelSensor)}
                        onChange={e => handleToggleSensor('fuel', e.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Typography style={{ fontSize: 13, fontWeight: 600, color: '#b45309' }}>
                        Cảm biến Xăng Dầu
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(form.hasTempSensor)}
                        onChange={e => handleToggleSensor('temp', e.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Typography style={{ fontSize: 13, fontWeight: 600, color: '#0369a1' }}>
                        Cảm biến Nhiệt Độ
                      </Typography>
                    }
                  />
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" style={{ fontSize: 13, color: '#555', marginBottom: 4 }}>
                  Mã IMEI / Serial Thiết bị
                </Typography>
                <TextField
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={form.serialImei}
                  onChange={e => setForm({ ...form, serialImei: e.target.value })}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" style={{ fontSize: 13, color: '#555', marginBottom: 4 }}>
                  Thời gian hẹn thi công
                </Typography>
                <TextField
                  type="datetime-local"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={form.scheduledTime}
                  onChange={e => setForm({ ...form, scheduledTime: e.target.value })}
                />
              </Grid>
            </Grid>
          </Box>

          <Divider style={{ margin: '16px 0' }} />

          {/* Nhóm 3: Thanh toán & Phí lắp đặt tự động tính theo Thiết bị & Cảm biến */}
          <Box mb={2}>
            <Typography variant="subtitle1" style={{ fontWeight: 700, color: '#d32f2f', marginBottom: 12 }}>
              3. THÔNG TIN THANH TOÁN & THU TIỀN HỘ SALE
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" style={{ fontSize: 13, color: '#555', marginBottom: 4 }}>
                  Hình thức thu tiền
                </Typography>
                <FormControl variant="outlined" size="small" fullWidth>
                  <Select
                    value={form.paymentMethod || 'CASH_KTV'}
                    onChange={e => {
                      const method = e.target.value;
                      setForm(prev => ({
                        ...prev,
                        paymentMethod: method,
                        cashCollectedByTech: method === 'CASH_KTV' ? prev.fee : prev.cashCollectedByTech
                      }));
                    }}
                  >
                    <MenuItem value="CASH_KTV">Tiền mặt (KTV thu hộ Sale)</MenuItem>
                    <MenuItem value="BANK_SALE">Chuyển khoản (Khách CK cho Sale)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={3}>
                <Typography variant="subtitle2" style={{ fontSize: 13, color: '#555', marginBottom: 4 }}>
                  Phí lắp đặt (VNĐ)
                </Typography>
                <TextField
                  variant="outlined"
                  size="small"
                  fullWidth
                  placeholder="0"
                  value={formatInputNumber(form.fee)}
                  onChange={e => {
                    const val = parseInputNumber(e.target.value);
                    setForm(prev => ({
                      ...prev,
                      fee: val,
                      cashCollectedByTech: prev.paymentMethod === 'CASH_KTV' ? val : prev.cashCollectedByTech
                    }));
                  }}
                />
              </Grid>

              {form.paymentMethod === 'CASH_KTV' && (
                <Grid item xs={12} sm={3}>
                  <Typography variant="subtitle2" style={{ fontSize: 13, color: '#555', marginBottom: 4 }}>
                    Tiền KTV thu mặt hộ (VNĐ)
                  </Typography>
                  <TextField
                    variant="outlined"
                    size="small"
                    fullWidth
                    placeholder="0"
                    value={formatInputNumber(form.cashCollectedByTech)}
                    onChange={e => {
                      const val = parseInputNumber(e.target.value);
                      setForm(prev => ({
                        ...prev,
                        cashCollectedByTech: val
                      }));
                    }}
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <Typography variant="subtitle2" style={{ fontSize: 13, color: '#555', marginBottom: 4 }}>
                  Ghi chú thi công (Yêu cầu kỹ thuật, giấu nguồn, gọi điện trước...)
                </Typography>
                <TextField
                  variant="outlined"
                  size="small"
                  multiline
                  rows={3}
                  fullWidth
                  value={form.notes}
                  onChange={e => setForm({ ...form, notes: e.target.value })}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions style={{ padding: '16px 24px', borderTop: '1px solid #e0e3ef' }}>
          <Button onClick={() => setOpenAddModal(false)} style={{ textTransform: 'none', color: '#666' }}>
            Hủy bỏ
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveForm}
            disabled={submittingForm}
            style={{ borderRadius: 8, textTransform: 'none', fontWeight: 700, padding: '8px 24px', backgroundColor: '#d32f2f', color: '#fff' }}
          >
            {submittingForm ? 'Đang xử lý...' : (isEditing ? 'Lưu cập nhật' : 'Tạo phiếu lắp đặt')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 🖼️ 5. MODAL THƯ VIỆN ẢNH NGHIỆM THU */}
      <Dialog open={openGalleryModal} onClose={() => setOpenGalleryModal(false)} maxWidth="md" fullWidth>
        <DialogTitle style={{ borderBottom: '1px solid #e2e8f0', padding: '16px 24px' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6" style={{ fontWeight: 800, color: '#1a1f36' }}>
                Ảnh Nghiệm Thu Thi Công [{selectedTicket?.id}]
              </Typography>
              <Typography variant="caption" style={{ color: '#64748b' }}>
                Khách hàng: {selectedTicket?.customerName} | Biển số: {selectedTicket?.licensePlate || 'N/A'} | Mã TB: {selectedTicket?.deviceCode}
              </Typography>
            </Box>
            <Button
              variant="contained"
              size="small"
              startIcon={<PhotoCameraIcon fontSize="small" />}
              onClick={() => handleTriggerUpload(selectedTicket)}
              style={{
                backgroundColor: '#d32f2f',
                color: '#fff',
                textTransform: 'none',
                borderRadius: 8,
                fontWeight: 700,
                padding: '6px 16px',
                boxShadow: 'none'
              }}
            >
              + Thêm ảnh mới
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent style={{ padding: 24, backgroundColor: '#fcfcfd' }}>
          {selectedTicket?.images && selectedTicket.images.length > 0 ? (
            <Box display="flex" justifyContent="center" width="100%">
              <Grid container spacing={2} justify="center" alignItems="center" style={{ maxWidth: '100%' }}>
                {selectedTicket.images.map((imgItem, idx) => {
                  const rawImgUrl = typeof imgItem === 'string' ? imgItem : (imgItem.url || imgItem.image_url || imgItem.path || '');
                  const imgUrl = getImageUrl(rawImgUrl);
                  const total = selectedTicket.images.length;
                  let gridCols = { xs: 12, sm: 6, md: 4 };
                  let imgHeight = 200;
                  if (total === 1) {
                    gridCols = { xs: 12, sm: 8, md: 6 };
                    imgHeight = 280;
                  } else if (total === 2) {
                    gridCols = { xs: 12, sm: 6, md: 5 };
                    imgHeight = 240;
                  }

                  return (
                    <Grid item {...gridCols} key={idx} style={{ display: 'flex', justifyContent: 'center' }}>
                      <Paper style={{ width: '100%', borderRadius: 10, overflow: 'hidden', border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', position: 'relative' }}>
                        <img src={imgUrl} alt={`Nghiệm thu ${idx + 1}`} style={{ width: '100%', height: imgHeight, objectFit: 'cover', display: 'block' }} />
                        <Box padding="8px 14px" backgroundColor="#ffffff" display="flex" justifyContent="space-between" alignItems="center" borderTop="1px solid #f1f5f9">
                          <Typography variant="caption" style={{ fontWeight: 700, color: '#334155', fontSize: 13 }}>
                            Ảnh nghiệm thu {idx + 1}
                          </Typography>
                          <Tooltip title="Xóa ảnh này">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteImageFromTicket(selectedTicket, imgItem, idx)}
                              style={{ color: '#ef4444', padding: 4 }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          ) : (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              padding={6}
              style={{ border: '2px dashed #cbd5e1', borderRadius: 12, backgroundColor: '#ffffff', cursor: 'pointer' }}
              onClick={() => handleTriggerUpload(selectedTicket)}
            >
              <PhotoCameraIcon style={{ fontSize: 48, color: '#94a3b8', marginBottom: 8 }} />
              <Typography style={{ fontWeight: 700, color: '#334155', fontSize: 15 }}>
                Chưa có ảnh nghiệm thu thi công nào
              </Typography>
              <Typography variant="caption" style={{ color: '#64748b', marginTop: 4 }}>
                Bấm vào đây hoặc nút "+ Thêm ảnh mới" ở trên để tải ảnh nghiệm thu từ máy tính
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions style={{ padding: '12px 24px', borderTop: '1px solid #e2e8f0' }}>
          <Button onClick={() => setOpenGalleryModal(false)} style={{ textTransform: 'none', fontWeight: 600, color: '#64748b' }}>
            Đóng
          </Button>
        </DialogActions>
      </Dialog>

      {/* 📄 6. MODAL IN BIÊN BẢN NGHIỆM THU */}
      <Dialog open={openPrintModal} onClose={() => setOpenPrintModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle style={{ borderBottom: '1px solid #e2e8f0' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" style={{ fontWeight: 800 }}>
              Xem Trước Biên Bản Bàn Giao & Nghiệm Thu
            </Typography>
            <IconButton size="small" onClick={() => setOpenPrintModal(false)}><ClearIcon /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent style={{ padding: 24, backgroundColor: '#ffffff' }}>
          <Box className={classes.printHeader}>
            <Typography variant="h5" style={{ fontWeight: 800, color: '#1e293b' }}>
              CÔNG TY CỔ PHẦN THIẾT BỊ ĐỊNH VỊ VGPS
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Địa chỉ: 102 Khuất Duy Tiến, Thanh Xuân, Hà Nội | Hotline: 1900 6868
            </Typography>
            <Typography variant="h6" style={{ fontWeight: 800, color: '#d32f2f', marginTop: 15 }}>
              BIÊN BẢN NGHIỆM THU & BÀN GIAO LẮP ĐẶT
            </Typography>
            <Typography variant="caption" style={{ fontStyle: 'italic' }}>
              Mã phiếu: <strong>{selectedTicket?.id}</strong> | Ngày hẹn: <strong>{selectedTicket?.scheduledTime}</strong>
            </Typography>
          </Box>

          <Box className={classes.printSection}>
            <Typography variant="subtitle2" style={{ fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>
              I. THÔNG TIN KHÁCH HÀNG & ĐỐI TƯỢNG
            </Typography>
            <Typography variant="body2">Đối tượng lắp: <strong>{(selectedTicket?.targetType === 'VNT' || selectedTicket?.targetType === 'VMAKE') ? 'Lắp cho VMAKE' : 'Lắp cho Đại lý'}</strong></Typography>
            <Typography variant="body2">Khách hàng: <strong>{selectedTicket?.customerName}</strong></Typography>
            <Typography variant="body2">Số điện thoại: <strong>{selectedTicket?.customerPhone}</strong></Typography>
            <Typography variant="body2">Địa điểm lắp: <strong>{selectedTicket?.address}</strong></Typography>
            <Typography variant="body2">Sale lập phiếu: <strong>{selectedTicket?.createdBySale || 'N/A'}</strong></Typography>
          </Box>

          <Box className={classes.printSection}>
            <Typography variant="subtitle2" style={{ fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>
              II. THIẾT BỊ & PHƯƠNG TIỆN
            </Typography>
            <Typography variant="body2">Loại xe / Biển số: <strong>{selectedTicket?.licensePlate} ({selectedTicket?.vehicleType})</strong></Typography>
            <Typography variant="body2">Mã thiết bị: <strong>{selectedTicket?.deviceCode} ({selectedTicket?.deviceTypeName})</strong></Typography>
            <Typography variant="body2">Cảm biến kèm theo: <strong>
              {[
                selectedTicket?.hasFuelSensor ? 'Cảm biến xăng dầu' : null,
                selectedTicket?.hasTempSensor ? 'Cảm biến nhiệt độ' : null
              ].filter(Boolean).join(', ') || 'Không lắp cảm biến phụ'}
            </strong></Typography>
            <Typography variant="body2">Mã IMEI/Serial: <strong>{selectedTicket?.serialImei}</strong></Typography>
            <Typography variant="body2">Kỹ thuật viên thi công: <strong>{selectedTicket?.technician}</strong></Typography>
          </Box>

          <Box className={classes.printSection}>
            <Typography variant="subtitle2" style={{ fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>
              III. THANH TOÁN & BÀN GIAO
            </Typography>
            <Typography variant="body2">Hình thức thanh toán: <strong>{selectedTicket?.paymentMethod === 'CASH_KTV' ? 'Tiền mặt (KTV thu hộ Sale)' : 'Khách chuyển khoản cho Sale'}</strong></Typography>
            <Typography variant="body2">Số tiền thu hộ: <strong>{formatMoney(selectedTicket?.cashCollectedByTech || selectedTicket?.fee || 0)}</strong></Typography>
            <Typography variant="body2" style={{ fontSize: 13, color: '#334155', marginTop: 4 }}>
              Thiết bị đã được kiểm tra tín hiệu hoạt động bình thường trên hệ thống VGPS. Khách hàng đã nhận bàn giao và hướng dẫn sử dụng.
            </Typography>
          </Box>

          <Grid container spacing={2} style={{ marginTop: 20, textAlign: 'center' }}>
            <Grid item xs={6}>
              <Typography variant="body2" style={{ fontWeight: 700 }}>ĐẠI DIỆN KHÁCH HÀNG</Typography>
              <Typography variant="caption" style={{ fontStyle: 'italic', display: 'block', marginBottom: 40 }}>(Ký & ghi rõ họ tên)</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" style={{ fontWeight: 700 }}>KỸ THUẬT VIÊN LẮP ĐẶT</Typography>
              <Typography variant="caption" style={{ fontStyle: 'italic', display: 'block', marginBottom: 40 }}>(Ký & ghi rõ họ tên)</Typography>
              <Typography variant="body2" style={{ fontWeight: 600 }}>{selectedTicket?.technician}</Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions style={{ padding: '16px 24px', borderTop: '1px solid #e2e8f0' }}>
          <Button onClick={() => setOpenPrintModal(false)} style={{ textTransform: 'none' }}>Đóng</Button>
          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={() => window.print()}
            style={{ borderRadius: 8, textTransform: 'none', fontWeight: 700, backgroundColor: '#d32f2f', color: '#fff' }}
          >
            In biên bản
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InstallationsTable;
